import React, { Suspense, type JSX } from "react"
import {
  $applyNodeReplacement,
  DecoratorNode,
  type DOMExportOutput,
  type LexicalNode,
  type NodeKey,
  type SerializedLexicalNode,
  type Spread,
} from "lexical"

const CitationBadgeComponent = React.lazy(() => import("./component"))

export interface Citation {
  url: string
  title: string
  snippet: string
}

type SerializedCitationNode = Spread<
  {
    citations: Citation[]
  },
  SerializedLexicalNode
>

export class CitationNode extends DecoratorNode<JSX.Element> {
  __citations: Citation[]

  static getType(): string {
    return "citation"
  }

  static clone(node: CitationNode): CitationNode {
    return new CitationNode(node.__citations, node.__key)
  }

  constructor(citations: Citation[], key?: NodeKey) {
    super(key)
    this.__citations = citations
  }

  exportDOM(): DOMExportOutput {
    // Export as empty text for copy/paste
    const textNode = document.createTextNode("")
    return { element: textNode as unknown as HTMLElement }
  }

  createDOM(): HTMLElement {
    const span = document.createElement("span")
    span.style.display = "inline"
    return span
  }

  updateDOM(): false {
    return false
  }

  isInline(): boolean {
    return true
  }

  decorate(): JSX.Element {
    return (
      <Suspense fallback={<span className="text-muted-foreground text-xs">...</span>}>
        <CitationBadgeComponent citations={this.__citations} />
      </Suspense>
    )
  }

  getTextContent(): string {
    return ""
  }

  exportJSON(): SerializedCitationNode {
    return {
      ...super.exportJSON(),
      citations: this.__citations,
      type: "citation",
      version: 1,
    }
  }

  static importJSON(serializedNode: SerializedCitationNode): CitationNode {
    return $createCitationNode(serializedNode.citations)
  }
}

export function $createCitationNode(citations: Citation[]): CitationNode {
  return $applyNodeReplacement(new CitationNode(citations))
}

export function $isCitationNode(node: LexicalNode | null | undefined): node is CitationNode {
  return node instanceof CitationNode
}
